"use client";

import React, { useState, useMemo, useCallback } from "react";
import { NamedSymbolIcon } from "@/components/common";
import { TableCell, TableRow } from "@/shadcn-components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import { Badge } from "@/shadcn-components/ui/badge";
import { Button } from "@/shadcn-components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shadcn-components/ui/context-menu";
import { Input } from "@/shadcn-components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import {
  Filter,
  Activity,
  Download,
  Info,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  BarChart3,
} from "lucide-react";

import {
  useFilterStore,
  DATE_FILTER_OPTIONS,
} from "@/vybe-trader-client/trade-history/stores/useFilterStore";
import {
  useFilterPositions,
  useFilterOrders,
  useFilterDeals,
} from "@/vybe-trader-client/trade-history/hooks/useFilterTrades";

import type {
  TradeOrder,
  VybeOrderInternal,
  VybeDealInternal,
} from "@/vybe-trader-client/trade-history/types";
import {
  formatOrderType,
  formatDealType,
  formatDirection,
  dealOpenTimeMs,
  orderTypeBuySellClass,
  dealTypeBuySellClass,
} from "@/vybe-trader-client/trade-history/types";
import HistoryDetailsDialog from "@/components/history-view/content/HistoryDetailsDialog";
import HistoryAnalyticsModal from "@/components/history-view/analytics/HistoryAnalyticsModal";
import { formatHistoryLocalDateTimeParts as formatDate } from "@/components/history-view/historyDateTimeFormat";
import {
  exportPositionsCsv,
  exportOrdersCsv,
  exportDealsCsv,
} from "@/components/history-view/historyCsvExport";
import { HistoryVirtualizedTable } from "@/components/history-view/content/HistoryVirtualizedTableBody";
import { DirectionStatusCell, deriveStatus } from "@/components/common";
import {
  historyTd,
  HISTORY_DEAL_COL_WIDTHS,
  HISTORY_ORDER_COL_WIDTHS,
  HISTORY_POSITION_COL_WIDTHS,
} from "@/components/history-view/content/historyDesktopTableLayout";
import {
  type PositionTableRow,
  type OrderTableRow,
  type DealTableRow,
  formatDateTimeCompact,
  formatHistoryPrice,
  positionPriceChangeParts,
  dealPriceChangeParts,
  processHistoryTableRows,
  getPositionRowSortValue,
  getPositionRowFilterHaystack,
  getOrderRowSortValue,
  getOrderRowFilterHaystack,
  getDealRowSortValue,
  getDealRowFilterHaystack,
} from "@/components/history-view/content/historyDesktopTableHelpers";
import { HistoryPositionsTableHeader } from "@/components/history-view/content/HistoryPositionsTableHeader";
import { HistoryOrdersTableHeader } from "@/components/history-view/content/HistoryOrdersTableHeader";
import { HistoryDealsTableHeader } from "@/components/history-view/content/HistoryDealsTableHeader";
import {
  computeHistoryDealsSummary,
  computeHistoryPositionsSummary,
  isPositionBalanceOperation,
} from "@/vybe-trader-client/trade-history/historySummary";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information";
import { formatSignedPnl } from "@/vybe-trader-client/opened-trades/utils";

export type TabType = "positions" | "orders" | "deals";

type DesktopHistoryDetail =
  | { kind: "position"; data: TradeOrder }
  | { kind: "order"; data: VybeOrderInternal }
  | { kind: "deal"; data: VybeDealInternal };

/** Execution / expiry column: execution is stored in seconds (see HistoryDetailsContent). */
function formatOrderSecondTime(order: VybeOrderInternal): string {
  if (order.executionTimeAsDateTime?.trim()) {
    const parsed = Date.parse(order.executionTimeAsDateTime);
    if (!Number.isNaN(parsed)) {
      return formatDateTimeCompact(formatDate(parsed));
    }
  }
  if (order.executionTime > 0) {
    return formatDateTimeCompact(formatDate(order.executionTime * 1000));
  }
  if (order.expirationTime > 0) {
    return formatDateTimeCompact(formatDate(order.expirationTime));
  }
  return "—";
}

function formatSummaryMoney(value: number): string {
  return formatSignedPnl(value);
}

function HistoryFundsSummary({
  deposits,
  withdrawals,
  netPnl,
  currency,
}: {
  deposits: number;
  withdrawals: number;
  netPnl: number;
  currency: string;
}) {
  return (
    <div className="flex items-center gap-2 text-[11px] tabular-nums leading-none">
      <span className="text-muted-foreground font-normal">
        Dep {formatSummaryMoney(deposits)} {currency}
      </span>
      <span className="text-muted-foreground font-normal">
        Wd -{withdrawals.toFixed(2)} {currency}
      </span>
      <span
        className={`font-semibold ${
          netPnl >= 0 ? "text-blue-400" : "text-red-400"
        }`}
      >
        {formatSummaryMoney(netPnl)} {currency}
      </span>
    </div>
  );
}

export default function TradeHistoryDesktopContent() {
  const [activeTab, setActiveTab] = useState<TabType>("positions");
  const [showFilterInputs, setShowFilterInputs] = React.useState(false);
  const [positionsSortColumn, setPositionsSortColumn] = React.useState<
    string | null
  >(null);
  const [positionsSortDirection, setPositionsSortDirection] = React.useState<
    "asc" | "desc"
  >("asc");
  const [positionsFilters, setPositionsFilters] = React.useState<
    Record<string, string>
  >({});
  const [ordersSortColumn, setOrdersSortColumn] = React.useState<string | null>(
    null
  );
  const [ordersSortDirection, setOrdersSortDirection] = React.useState<
    "asc" | "desc"
  >("asc");
  const [ordersFilters, setOrdersFilters] = React.useState<
    Record<string, string>
  >({});
  const [dealsSortColumn, setDealsSortColumn] = React.useState<string | null>(
    null
  );
  const [dealsSortDirection, setDealsSortDirection] = React.useState<
    "asc" | "desc"
  >("asc");
  const [dealsFilters, setDealsFilters] = React.useState<
    Record<string, string>
  >({});
  const [detail, setDetail] = useState<DesktopHistoryDetail | null>(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const accountCurrency = useAccountInformationStore(
    (state) => state.account?.currency || "USD"
  );
  const {
    selectedSymbol,
    dateFilter,
    customDateFrom,
    customDateTo,
    setSelectedSymbol,
    setDateFilter,
  } = useFilterStore();

  const filterCfg = {
    symbol: selectedSymbol,
    dateFilter,
    customDateFrom,
    customDateTo,
  };

  const positions = useFilterPositions(filterCfg);
  const orders = useFilterOrders(filterCfg);
  const deals = useFilterDeals(filterCfg);

  const positionsSummary = useMemo(
    () => computeHistoryPositionsSummary(positions),
    [positions]
  );

  const dealsSummary = useMemo(
    () => computeHistoryDealsSummary(deals),
    [deals]
  );

  const positionTableData: PositionTableRow[] = useMemo(() => {
    return positions.map((position) => {
      const isBalanceOperation = isPositionBalanceOperation(position);

      const displayType = isBalanceOperation
        ? formatDealType(position.dealType)
        : formatOrderType(position.orderType);

      return {
        ...position,
        formattedOpenTime: formatDate(position.openTime),
        formattedCloseTime: formatDate(position.closeTime),
        displayType,
        isBalanceOperation,
      };
    });
  }, [positions]);

  const orderTableData: OrderTableRow[] = useMemo(() => {
    return orders.map((order) => {
      const getStatusColor = (state: number) => {
        switch (state) {
          case 4:
            return "text-blue-400";
          case 2:
            return "text-red-400";
          default:
            return "text-yellow-400";
        }
      };

      const getStatusLabel = (state: number) => {
        switch (state) {
          case 4:
            return "Filled";
          case 2:
            return "Cancelled";
          default:
            return "Pending";
        }
      };

      return {
        ...order,
        formattedTime: formatDate(order.openTimeMs),
        formattedSecondTime: formatOrderSecondTime(order),
        displayType: formatOrderType(order.type),
        statusLabel: getStatusLabel(order.state),
        statusColor: getStatusColor(order.state),
      };
    });
  }, [orders]);

  const dealTableData: DealTableRow[] = useMemo(() => {
    return deals.map((deal) => ({
      ...deal,
      formattedTime: formatDate(dealOpenTimeMs(deal)),
      displayType: formatDealType(deal.type),
    }));
  }, [deals]);

  const processedPositionRows = useMemo(
    () =>
      processHistoryTableRows(
        positionTableData,
        positionsSortColumn,
        positionsSortDirection,
        positionsFilters,
        getPositionRowSortValue,
        getPositionRowFilterHaystack
      ),
    [
      positionTableData,
      positionsSortColumn,
      positionsSortDirection,
      positionsFilters,
    ]
  );

  const processedOrderRows = useMemo(
    () =>
      processHistoryTableRows(
        orderTableData,
        ordersSortColumn,
        ordersSortDirection,
        ordersFilters,
        getOrderRowSortValue,
        getOrderRowFilterHaystack
      ),
    [orderTableData, ordersSortColumn, ordersSortDirection, ordersFilters]
  );

  const processedDealRows = useMemo(
    () =>
      processHistoryTableRows(
        dealTableData,
        dealsSortColumn,
        dealsSortDirection,
        dealsFilters,
        getDealRowSortValue,
        getDealRowFilterHaystack
      ),
    [dealTableData, dealsSortColumn, dealsSortDirection, dealsFilters]
  );

  const hasActivePositionsFilters = Object.values(positionsFilters).some(
    (v) => v !== ""
  );
  const hasActiveOrdersFilters = Object.values(ordersFilters).some(
    (v) => v !== ""
  );
  const hasActiveDealsFilters = Object.values(dealsFilters).some((v) => v !== "");

  const hasActiveColumnFilters =
    activeTab === "positions"
      ? hasActivePositionsFilters
      : activeTab === "orders"
        ? hasActiveOrdersFilters
        : hasActiveDealsFilters;

  const summary = useMemo(() => {
    switch (activeTab) {
      case "positions":
        return {
          kind: "positions" as const,
          ...positionsSummary,
        };
      case "orders": {
        const ordData = orderTableData;
        return {
          kind: "orders" as const,
          total: ordData.length,
          filled: ordData.filter((o) => o.state === 4).length,
          cancelled: ordData.filter((o) => o.state === 2).length,
          pending: ordData.filter((o) => o.state === 0 || o.state === 1).length,
        };
      }
      case "deals":
        return {
          kind: "deals" as const,
          ...dealsSummary,
        };
      default:
        return { kind: "none" as const, total: 0 };
    }
  }, [
    activeTab,
    positionsSummary,
    dealsSummary,
    orderTableData,
  ]);

  const symbolOptions = [
    { value: "allsymbols", label: "All" },
    { value: "EURUSD", label: "EUR/USD" },
    { value: "GBPUSD", label: "GBP/USD" },
    { value: "USDJPY", label: "USD/JPY" },
    { value: "AUDUSD", label: "AUD/USD" },
  ];

  const toggleFilterInputs = () => {
    setShowFilterInputs(!showFilterInputs);
  };

  const handlePositionsSort = (column: string) => {
    if (positionsSortColumn === column) {
      setPositionsSortDirection(
        positionsSortDirection === "asc" ? "desc" : "asc"
      );
    } else {
      setPositionsSortColumn(column);
      setPositionsSortDirection("asc");
    }
  };

  const handleOrdersSort = (column: string) => {
    if (ordersSortColumn === column) {
      setOrdersSortDirection(ordersSortDirection === "asc" ? "desc" : "asc");
    } else {
      setOrdersSortColumn(column);
      setOrdersSortDirection("asc");
    }
  };

  const handleDealsSort = (column: string) => {
    if (dealsSortColumn === column) {
      setDealsSortDirection(dealsSortDirection === "asc" ? "desc" : "asc");
    } else {
      setDealsSortColumn(column);
      setDealsSortDirection("asc");
    }
  };

  const handlePositionsFilterChange = (column: string, value: string) => {
    setPositionsFilters((prev) => ({ ...prev, [column]: value }));
  };

  const handleOrdersFilterChange = (column: string, value: string) => {
    setOrdersFilters((prev) => ({ ...prev, [column]: value }));
  };

  const handleDealsFilterChange = (column: string, value: string) => {
    setDealsFilters((prev) => ({ ...prev, [column]: value }));
  };

  const clearColumnFilters = () => {
    switch (activeTab) {
      case "positions":
        setPositionsFilters({});
        break;
      case "orders":
        setOrdersFilters({});
        break;
      case "deals":
        setDealsFilters({});
        break;
    }
  };

  const renderSortIcon = (
    column: string,
    sortColumn: string | null,
    sortDirection: "asc" | "desc"
  ) => {
    if (sortColumn !== column)
      return <ArrowUpDown className="ml-1 h-2.5 w-2.5 shrink-0 opacity-60" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-1 h-2.5 w-2.5 shrink-0" />
    ) : (
      <ArrowDown className="ml-1 h-2.5 w-2.5 shrink-0" />
    );
  };

  const renderFilterDropdown = (
    column: string,
    placeholder: string,
    filters: Record<string, string>,
    onFilterChange: (column: string, value: string) => void
  ) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-3.5 w-3.5 p-0 ml-0.5 ${
            filters[column] ? "text-blue-600" : "text-muted-foreground"
          }`}
        >
          <Filter className="h-2.5 w-2.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-48" align="start">
        <div className="p-1.5">
          <Input
            placeholder={placeholder}
            value={filters[column] || ""}
            onChange={(e) => onFilterChange(column, e.target.value)}
            className="h-7 text-[11px] px-2"
            autoFocus
          />
          {filters[column] && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-1.5 h-5 w-full text-[11px]"
              onClick={() => onFilterChange(column, "")}
            >
              Clear filter
            </Button>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const positionsTableHeader = useMemo(
    () => (
      <HistoryPositionsTableHeader
        sortColumn={positionsSortColumn}
        sortDirection={positionsSortDirection}
        filters={positionsFilters}
        showFilterInputs={showFilterInputs}
        onSort={handlePositionsSort}
        onFilterChange={handlePositionsFilterChange}
        renderSortIcon={(column) =>
          renderSortIcon(column, positionsSortColumn, positionsSortDirection)
        }
        renderFilterDropdown={(column, placeholder) =>
          renderFilterDropdown(
            column,
            placeholder,
            positionsFilters,
            handlePositionsFilterChange
          )
        }
      />
    ),
    [
      positionsSortColumn,
      positionsSortDirection,
      positionsFilters,
      showFilterInputs,
    ]
  );

  const ordersTableHeader = useMemo(
    () => (
      <HistoryOrdersTableHeader
        sortColumn={ordersSortColumn}
        sortDirection={ordersSortDirection}
        filters={ordersFilters}
        showFilterInputs={showFilterInputs}
        onSort={handleOrdersSort}
        onFilterChange={handleOrdersFilterChange}
        renderSortIcon={(column) =>
          renderSortIcon(column, ordersSortColumn, ordersSortDirection)
        }
        renderFilterDropdown={(column, placeholder) =>
          renderFilterDropdown(
            column,
            placeholder,
            ordersFilters,
            handleOrdersFilterChange
          )
        }
      />
    ),
    [ordersSortColumn, ordersSortDirection, ordersFilters, showFilterInputs]
  );

  const dealsTableHeader = useMemo(
    () => (
      <HistoryDealsTableHeader
        sortColumn={dealsSortColumn}
        sortDirection={dealsSortDirection}
        filters={dealsFilters}
        showFilterInputs={showFilterInputs}
        onSort={handleDealsSort}
        onFilterChange={handleDealsFilterChange}
        renderSortIcon={(column) =>
          renderSortIcon(column, dealsSortColumn, dealsSortDirection)
        }
        renderFilterDropdown={(column, placeholder) =>
          renderFilterDropdown(
            column,
            placeholder,
            dealsFilters,
            handleDealsFilterChange
          )
        }
      />
    ),
    [dealsSortColumn, dealsSortDirection, dealsFilters, showFilterInputs]
  );

  const handleExportCsv = useCallback(() => {
    switch (activeTab) {
      case "positions":
        exportPositionsCsv(positions);
        break;
      case "orders":
        exportOrdersCsv(orders);
        break;
      case "deals":
        exportDealsCsv(deals);
        break;
      default:
        break;
    }
  }, [activeTab, positions, orders, deals]);

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="h-8 shrink-0 px-2 border-b border-border bg-muted/20 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Select
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as TabType)}
          >
            <SelectTrigger className="h-5 w-[4.5rem] text-[11px] px-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="positions">Positions</SelectItem>
              <SelectItem value="orders">Orders</SelectItem>
              <SelectItem value="deals">Deals</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedSymbol} onValueChange={setSelectedSymbol}>
            <SelectTrigger className="h-5 w-14 text-[11px] px-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {symbolOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="h-5 min-w-[4rem] max-w-[7rem] text-[11px] px-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Badge
            variant="outline"
            className="h-4 px-1 py-0 text-[10px] leading-none font-normal"
          >
            {summary.total} Total
          </Badge>

          {activeTab === "positions" && summary.kind === "positions" && (
            <>
              <Badge
                variant="outline"
                className="h-4 px-1 py-0 text-[10px] leading-none font-normal text-blue-600 border-blue-400/30"
              >
                {summary.profitable} ↗
              </Badge>
              <Badge
                variant="outline"
                className="h-4 px-1 py-0 text-[10px] leading-none font-normal text-red-600 border-red-400/30"
              >
                {summary.losing} ↘
              </Badge>
            </>
          )}

          {activeTab === "orders" && summary.kind === "orders" && (
            <>
              <Badge
                variant="outline"
                className="h-4 px-1 py-0 text-[10px] leading-none font-normal text-blue-600 border-blue-400/30"
              >
                {summary.filled} Filled
              </Badge>
              <Badge
                variant="outline"
                className="h-4 px-1 py-0 text-[10px] leading-none font-normal text-red-600 border-red-400/30"
              >
                {summary.cancelled} Cancelled
              </Badge>
              <Badge
                variant="outline"
                className="h-4 px-1 py-0 text-[10px] leading-none font-normal text-yellow-600 border-yellow-400/30"
              >
                {summary.pending} Pending
              </Badge>
            </>
          )}

          {activeTab === "deals" && summary.kind === "deals" && (
            <>
              <Badge
                variant="outline"
                className="h-4 px-1 py-0 text-[10px] leading-none font-normal text-blue-600 border-blue-400/30"
              >
                {summary.profitable} ↗
              </Badge>
              <Badge
                variant="outline"
                className="h-4 px-1 py-0 text-[10px] leading-none font-normal text-red-600 border-red-400/30"
              >
                {summary.losing} ↘
              </Badge>
            </>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-5 px-1.5 text-[11px] gap-0.5"
            title={`Export ${activeTab} to CSV`}
            onClick={handleExportCsv}
          >
            <Download className="h-2.5 w-2.5 mr-0.5" />
            Export
          </Button>
          <Button
            variant={showFilterInputs ? "default" : "ghost"}
            size="sm"
            className="h-5 px-1.5 text-[11px] gap-0.5"
            onClick={toggleFilterInputs}
            title={
              showFilterInputs ? "Hide filter inputs" : "Show filter inputs"
            }
          >
            <Filter className="h-2.5 w-2.5 mr-0.5" />
            {showFilterInputs ? "Hide" : "Filter"}
          </Button>
          {hasActiveColumnFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1.5 text-[11px] text-red-600"
              onClick={clearColumnFilters}
              title="Clear all column filters"
            >
              Clear
            </Button>
          )}
          {activeTab === "positions" && summary.kind === "positions" && (
            <HistoryFundsSummary
              deposits={summary.deposits}
              withdrawals={summary.withdrawals}
              netPnl={summary.netPnl}
              currency={accountCurrency}
            />
          )}
          {activeTab === "deals" && summary.kind === "deals" && (
            <HistoryFundsSummary
              deposits={summary.deposits}
              withdrawals={summary.withdrawals}
              netPnl={summary.netPnl}
              currency={accountCurrency}
            />
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {activeTab === "positions" && renderPositionsTable()}
        {activeTab === "orders" && renderOrdersTable()}
        {activeTab === "deals" && renderDealsTable()}
      </div>

      <HistoryDetailsDialog
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        kind={detail?.kind ?? null}
        position={detail?.kind === "position" ? detail.data : null}
        order={detail?.kind === "order" ? detail.data : null}
        deal={detail?.kind === "deal" ? detail.data : null}
      />

      <HistoryAnalyticsModal
        open={analyticsOpen}
        onOpenChange={setAnalyticsOpen}
        initialScope={filterCfg}
      />
    </div>
  );

  function renderPositionsTable() {
    const W = HISTORY_POSITION_COL_WIDTHS;
    const emptyMessage =
      positionTableData.length === 0
        ? "No positions found"
        : hasActivePositionsFilters && processedPositionRows.length === 0
          ? "No positions match your filters"
          : "No positions found";

    return (
      <HistoryVirtualizedTable
        rowCount={processedPositionRows.length}
        colSpan={14}
        emptyMessage={emptyMessage}
        header={positionsTableHeader}
        getRowKey={(index) => processedPositionRows[index].ticket}
        renderRow={(index) => {
          const position = processedPositionRows[index];
          const change = positionPriceChangeParts(
            position,
            position.isBalanceOperation
          );
          const chgClass =
            change.numeric === null
              ? "text-muted-foreground"
              : change.numeric === 0
              ? "text-foreground"
              : change.numeric > 0
              ? "text-blue-400"
              : "text-red-400";

          return (
            <ContextMenu>
              <ContextMenuTrigger asChild>
                <TableRow className="h-7 hover:bg-accent transition-colors cursor-context-menu">
                  <TableCell
                    className={`${historyTd} ${W.openTime} text-muted-foreground`}
                  >
                    {formatDateTimeCompact(position.formattedOpenTime)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.symbol}`}>
                    <span className="inline-flex items-center gap-1.5 font-semibold">
                      <NamedSymbolIcon name={position.symbol} />
                      {position.symbol}
                    </span>
                  </TableCell>
                  <TableCell
                    className={`${historyTd} ${W.ticket} font-mono text-muted-foreground`}
                  >
                    #{position.ticket}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.type}`}>
                    <span
                      className={orderTypeBuySellClass(position.orderType)}
                    >
                      {position.displayType}
                    </span>
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.lots} text-right font-mono`}>
                    {position.lots}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.openPrice} text-right font-mono`}>
                    {position.isBalanceOperation
                      ? "-"
                      : position.openPrice.toFixed(position.digits)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.tp} text-right font-mono`}>
                    {formatHistoryPrice(
                      position.takeProfit,
                      position.digits
                    )}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.sl} text-right font-mono`}>
                    {formatHistoryPrice(position.stopLoss, position.digits)}
                  </TableCell>
                  <TableCell
                    className={`${historyTd} ${W.closeTime} text-muted-foreground`}
                  >
                    {formatDateTimeCompact(position.formattedCloseTime)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.closePrice} text-right font-mono`}>
                    {position.isBalanceOperation
                      ? "-"
                      : position.closePrice.toFixed(position.digits)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.commission} text-right font-mono`}>
                    {position.commission.toFixed(2)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.swap} text-right font-mono`}>
                    {position.swap.toFixed(2)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.pnl} text-right font-semibold`}>
                    <span
                      className={
                        position.profit >= 0 ? "text-blue-400" : "text-red-400"
                      }
                    >
                      {position.profit >= 0 ? "+" : ""}
                      {position.profit.toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell
                    className={`${historyTd} ${W.change} text-right font-mono ${chgClass}`}
                  >
                    {change.display}
                  </TableCell>
                </TableRow>
              </ContextMenuTrigger>
              <ContextMenuContent>
                <ContextMenuItem
                  onSelect={() =>
                    setDetail({ kind: "position", data: position })
                  }
                >
                  <Info className="mr-2 h-4 w-4" />
                  Show details
                </ContextMenuItem>
                <ContextMenuItem onSelect={() => setAnalyticsOpen(true)}>
                  <BarChart3 className="mr-2 h-4 w-4" />
                  View analytics
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          );
        }}
      />
    );
  }

  function renderOrdersTable() {
    const W = HISTORY_ORDER_COL_WIDTHS;
    const emptyMessage =
      orderTableData.length === 0
        ? "No orders found"
        : hasActiveOrdersFilters && processedOrderRows.length === 0
          ? "No orders match your filters"
          : "No orders found";

    return (
      <HistoryVirtualizedTable
        rowCount={processedOrderRows.length}
        colSpan={11}
        emptyMessage={emptyMessage}
        header={ordersTableHeader}
        getRowKey={(index) =>
          processedOrderRows[index].ticketNumber ??
          processedOrderRows[index].ticket
        }
        renderRow={(index) => {
          const order = processedOrderRows[index];
          return (
            <ContextMenu>
              <ContextMenuTrigger asChild>
                <TableRow className="h-7 hover:bg-accent transition-colors cursor-context-menu">
                  <TableCell
                    className={`${historyTd} ${W.open} text-muted-foreground`}
                  >
                    {formatDateTimeCompact(order.formattedTime)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.status}`}>
                    <DirectionStatusCell
                      status={deriveStatus(order.state)}
                      direction={
                        orderTypeBuySellClass(order.type) === "text-red-400"
                          ? "sell"
                          : "buy"
                      }
                    />
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.symbol}`}>
                    <span className="inline-flex items-center gap-1.5 font-semibold">
                      <NamedSymbolIcon name={order.symbol} />
                      {order.symbol}
                    </span>
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.ticket} text-right font-mono`}>
                    {order.ticketNumber ?? order.ticket}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.type}`}>
                    <span className={orderTypeBuySellClass(order.type)}>
                      {order.displayType}
                    </span>
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.lots} text-right font-mono`}>
                    {order.lots}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.price} text-right font-mono`}>
                    {order.price > 0 ? order.price.toFixed(order.digits) : "-"}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.tp} text-right font-mono`}>
                    {formatHistoryPrice(order.takeProfit, order.digits)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.sl} text-right font-mono`}>
                    {formatHistoryPrice(order.stopLoss, order.digits)}
                  </TableCell>
                  <TableCell
                    className={`${historyTd} ${W.exec} text-muted-foreground`}
                  >
                    {order.formattedSecondTime}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.state}`}>
                    <span className={order.statusColor}>{order.statusLabel}</span>
                  </TableCell>
                </TableRow>
              </ContextMenuTrigger>
              <ContextMenuContent>
                <ContextMenuItem
                  onSelect={() => setDetail({ kind: "order", data: order })}
                >
                  <Info className="mr-2 h-4 w-4" />
                  Show details
                </ContextMenuItem>
                <ContextMenuItem onSelect={() => setAnalyticsOpen(true)}>
                  <BarChart3 className="mr-2 h-4 w-4" />
                  View analytics
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          );
        }}
      />
    );
  }

  function renderDealsTable() {
    const W = HISTORY_DEAL_COL_WIDTHS;
    const emptyMessage =
      dealTableData.length === 0
        ? "No deals found"
        : hasActiveDealsFilters && processedDealRows.length === 0
          ? "No deals match your filters"
          : "No deals found";

    return (
      <HistoryVirtualizedTable
        rowCount={processedDealRows.length}
        colSpan={12}
        emptyMessage={emptyMessage}
        header={dealsTableHeader}
        getRowKey={(index) => processedDealRows[index].ticketNumber}
        renderRow={(index) => {
          const deal = processedDealRows[index];
          const change = dealPriceChangeParts(deal);
          const chgClass =
            change.numeric === null
              ? "text-muted-foreground"
              : change.numeric === 0
              ? "text-foreground"
              : change.numeric > 0
              ? "text-blue-400"
              : "text-red-400";

          return (
            <ContextMenu>
              <ContextMenuTrigger asChild>
                <TableRow className="h-7 hover:bg-accent transition-colors cursor-context-menu">
                  <TableCell
                    className={`${historyTd} ${W.time} text-muted-foreground`}
                  >
                    {formatDateTimeCompact(deal.formattedTime)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.symbol}`}>
                    <span className="inline-flex items-center gap-1.5 font-semibold">
                      <NamedSymbolIcon name={deal.symbol} />
                      {deal.symbol}
                    </span>
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.deal} text-right font-mono`}>
                    {deal.ticketNumber}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.type}`}>
                    <span className={dealTypeBuySellClass(deal.type)}>
                      {deal.displayType}
                    </span>
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.direction} text-muted-foreground`}>
                    {formatDirection(deal.direction)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.lots} text-right font-mono`}>
                    {deal.lots}
                  </TableCell>
                  <TableCell
                    className={`${historyTd} ${W.price} text-right font-mono font-semibold`}
                  >
                    {deal.price.toFixed(deal.digits)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.tp} text-right font-mono`}>
                    {formatHistoryPrice(deal.takeProfit, deal.digits)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.sl} text-right font-mono`}>
                    {formatHistoryPrice(deal.stopLoss, deal.digits)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.commission} text-right font-mono`}>
                    {deal.commission.toFixed(2)}
                  </TableCell>
                  <TableCell className={`${historyTd} ${W.pnl} text-right font-semibold`}>
                    <span
                      className={
                        deal.profit >= 0 ? "text-blue-400" : "text-red-400"
                      }
                    >
                      {deal.profit >= 0 ? "+" : ""}
                      {deal.profit.toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell
                    className={`${historyTd} ${W.change} text-right font-mono ${chgClass}`}
                  >
                    {change.display}
                  </TableCell>
                </TableRow>
              </ContextMenuTrigger>
              <ContextMenuContent>
                <ContextMenuItem
                  onSelect={() => setDetail({ kind: "deal", data: deal })}
                >
                  <Info className="mr-2 h-4 w-4" />
                  Show details
                </ContextMenuItem>
                <ContextMenuItem onSelect={() => setAnalyticsOpen(true)}>
                  <BarChart3 className="mr-2 h-4 w-4" />
                  View analytics
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          );
        }}
      />
    );
  }

}
